import React from 'react'
import styled, { SimpleInterpolation } from 'styled-components'
import VisibilitySensor from 'react-visibility-sensor'
import useSpecs, { SpecService } from '../hooks/use-specs'
import { Box, Text, theme, Chip } from '@containous/faency'
import { Link } from 'react-router-dom'
import { Message } from './Message'
import { useCurrentGroup } from '../hooks/use-current-group'

const Version = styled(Text)`
  font-size: 16px;
  opacity: 0.7;
  margin-left: 4px;
`

const GroupBadge = styled(Chip)`
  font-size: 11px;
  padding: 4px;
  line-height: 9px;
  height: 18px;
  border-radius: 4px;
`

const CustomLink = styled(Link)<{ isSelected: boolean }>`
  display: flex;
  text-decoration: none;
  padding: 16px;
  border-bottom: 1px solid ${theme.colors.grays[4]};
  flex-flow: column;
  transition: box-shadow 200ms ease-in-out, color 200ms ease-in-out, background 200ms ease-in-out;
  color: ${theme.colors.black};
  outline: none;

  ${({ isSelected }): SimpleInterpolation =>
    isSelected &&
    `
    background: ${theme.colors.white};
  `}

  &:hover, &:focus {
    box-shadow: 0 0 16px 0px ${theme.colors.grays[4]};
  }
`

const Container = styled(Box)`
  overflow-y: auto;
  padding: ${theme.space[1]} 0;
  max-height: calc(100vh - 64px /* header */ - 72px /* Search */ - 30px /* Title */);
`

type ServiceListProps = {
  search?: string | null
  currentService?: string
  openDrawer?: () => void
}

export const ServiceList: React.FC<ServiceListProps> = ({ search, currentService, openDrawer }) => {
  const { specs, message: error, size, setSize, isReachingEnd } = useSpecs(search || '')
  const [group, { isLoading }] = useCurrentGroup()

  const handleFetchMore = (isVisible: boolean): void => {
    if (isVisible && setSize && size) {
      setSize(size + 1)
    }
  }

  if (error) {
    return <Message>{error}</Message>
  }

  if (specs === undefined) {
    return <Message>Loading...</Message>
  }

  if (specs === null) {
    return <Message>No services found.</Message>
  }

  return (
    <Container>
      {specs?.length === 0 && <Message>There are no services to show.</Message>}

      {specs.map(({ title, rels, description, version, hideServiceNames }) => {
        const rel = rels[0]
        const top10Rels = rels.filter(rel => rel?.name !== undefined).slice(0, 10)
        const extraCount = Math.max(rels.length - top10Rels.length, 0)

        return (
          <CustomLink
            key={rel?.link}
            to={['/', rel?.name || '', search && `?search=${search}`].join('')}
            isSelected={currentService === rel?.name}
            onClick={openDrawer}
          >
            <div>
              <Text sx={{ fontWeight: 'bold', fontSize: 3, color: 'inherit' }}>{title}</Text>
              {version && <Version>{version}</Version>}
            </div>
            <Text sx={{ fontSize: 1, color: 'inherit' }} truncate>
              {description}
            </Text>
            {!hideServiceNames &&
              top10Rels?.length > 0 &&
              top10Rels.map(serviceSpec => (
                <Text key={serviceSpec.name} sx={{ fontSize: 0, color: theme.colors.grays[5], pt: 1 }}>
                  {serviceSpec.name}{' '}
                  {!isLoading && group === 'all' && !!serviceSpec?.group && (
                    <GroupBadge variant="blue">{serviceSpec.group}</GroupBadge>
                  )}
                </Text>
              ))}
            {!hideServiceNames && extraCount > 0 && (
              <Text sx={{ fontSize: 0, color: theme.colors.grays[5], pt: 1 }}>{extraCount} more</Text>
            )}
          </CustomLink>
        )
      })}
      {!isReachingEnd && (
        <VisibilitySensor onChange={handleFetchMore} partialVisibility>
          <Message>Loading...</Message>
        </VisibilitySensor>
      )}
    </Container>
  )
}
